import { Button, Modal } from '@platform/components';
import { Cohorts } from '@platform/client';
import {
  CohortResponse,
  GetCurrentUser200,
  ProfessionalApplicationParameters,
} from '@platform/client/schemas';
import { useState } from 'react';
import { ROUTES } from '../../routes/consts';
import { useApplicationLocalStorage } from '../../application/application-hook';
import { navigate } from 'wouter/use-browser-location';
import { useQueryClient } from '@tanstack/react-query';
import { ApplicationStorageDataKeys } from '../../application/Application';
import { PressEvent } from 'react-aria-components';
import Skeleton from 'react-loading-skeleton';

export const RejoinPanel = ({ user }: { user: GetCurrentUser200 }) => {
  const { data: rejoinData, isLoading } = Cohorts.useGetUserRejoinData();
  const cohortsOpen = rejoinData?.cohorts.length && rejoinData?.cohorts.length > 0;
  const userCohortCompleted = rejoinData?.lastCohortStatus === 'complete';
  if (isLoading) {
    return <InactiveSegmentSkeleton />;
  }
  return cohortsOpen ?
      <CohortsOpenSegment
        userCohortCompleted={userCohortCompleted}
        openCohorts={rejoinData?.cohorts ?? []}
        corporateClient={user?.corporatePartner}
      />
    : <CohortsClosedSegment
        userCohortCompleted={userCohortCompleted}
        corporateClient={user?.corporatePartner}
      />;
};

// add local storage flag to allow user to review and complete application
// otherwise their existing application status would direct to confirmation
export const REVIEW_MODE_STORAGE_KEY = 'reviewMode';
export type ReviewModeStorage = {
  reviewMode: boolean;
};

const CohortsOpenSegment = ({
  userCohortCompleted,
  openCohorts,
  corporateClient,
}: {
  userCohortCompleted: boolean;
  openCohorts: CohortResponse[];
  corporateClient?: string | null;
}) => {
  const redirectRoute =
    openCohorts.length > 1 || corporateClient == 'SAP' ?
      `/platform${ROUTES.COHORT_SELECTION}`
    : `/platform/welcome-back/${corporateClient}`;

  const { updateApplicationData: updateReviewMode } =
    useApplicationLocalStorage<ReviewModeStorage>(REVIEW_MODE_STORAGE_KEY);
  const { updateApplicationData } = useApplicationLocalStorage<ProfessionalApplicationParameters>(
    ApplicationStorageDataKeys.PROFESSIONAL_APPLICATION,
  );

  const onClick = () => {
    // DG TODO: this onClick shouldn't set reJoin for SAP - They will select on first page of application
    if (corporateClient !== 'SAP') {
      updateApplicationData({ cohortId: openCohorts[0]?.cohortId });
    }
    updateReviewMode({ reviewMode: true });
    navigate(redirectRoute);
  };

  return (
    <InactiveSegmentContainer>
      <InactiveSegmentHeader cohortsClosed={false} userCohortCompleted={userCohortCompleted} />
      <InactiveSegmentCtas
        buttonOnClick={onClick}
        buttonText='Get started'
        corporateClient={corporateClient}
      />
    </InactiveSegmentContainer>
  );
};

const CohortsClosedSegment = ({
  userCohortCompleted,
  corporateClient,
}: {
  userCohortCompleted: boolean;
  corporateClient?: string | null;
}) => {
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
  const { data, isSuccess } = Cohorts.useGetUserNotificationRequests();
  const notifyMeRequestRegistered = isSuccess && data.currentNotifications?.length ? true : false;
  const queryClient = useQueryClient();
  const requestNotification = Cohorts.useAddNotificationRequest({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [...Cohorts.getGetUserNotificationRequestsQueryKey()],
        });
        setIsNotifyModalOpen(true);
      },
    },
  });
  return (
    <InactiveSegmentContainer>
      <InactiveSegmentHeader cohortsClosed={true} userCohortCompleted={userCohortCompleted} />
      <InactiveSegmentCtas
        buttonOnClick={() => requestNotification.mutate()}
        buttonText={
          notifyMeRequestRegistered ? "You're on the list" : 'Notify me when a new cohort opens'
        }
        buttonDisabled={notifyMeRequestRegistered}
        corporateClient={corporateClient}
      />
      <NotifyMeModal isOpen={isNotifyModalOpen} onClose={() => setIsNotifyModalOpen(false)} />
    </InactiveSegmentContainer>
  );
};

export const InactiveSegmentContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className='w-full md:w-[700px] border border-gray-100 shadow-xs rounded-xl p-12 mt-4'>
      {children}
    </div>
  );
};

export const InactiveSegmentSkeleton = () => {
  return (
    <InactiveSegmentContainer>
      <Skeleton height={'24px'} width={'100%'} className='mb-4 mx-auto' />
      <Skeleton height={'180px'} width={'100%'} className='mb-4 mx-auto' />
    </InactiveSegmentContainer>
  );
};

const InactiveSegmentHeader = ({
  cohortsClosed,
  userCohortCompleted,
}: {
  cohortsClosed: boolean;
  userCohortCompleted: boolean;
}) => {
  const subText = getHeaderSubtext(cohortsClosed, userCohortCompleted);
  return (
    <div className='mb-8'>
      <h3 className='text-display-sm text-gray-700 font-semibold text-center mb-4'>
        You're not currently participating in a program.
      </h3>
      <p className='text-lg text-center text-gray-500'>{subText}</p>
    </div>
  );
};

const InactiveSegmentCtas = ({
  buttonOnClick,
  buttonText,
  buttonDisabled = false,
  corporateClient,
}: {
  buttonOnClick: (e: PressEvent) => void;
  buttonText: string;
  buttonDisabled?: boolean;
  corporateClient?: string | null;
}) => {
  return (
    <div className='text-center'>
      <Button
        variant='primary'
        onPress={buttonOnClick}
        size='sm'
        additionalClassnames='mb-[16px] w-full'
        isDisabled={buttonDisabled}
      >
        {buttonText}
      </Button>
      <LearnMoreLink corporateClient={corporateClient} />
    </div>
  );
};

const LearnMoreLink = ({ corporateClient }: { corporateClient?: string | null }) => {
  const learnMoreUrl = getLearnMoreUrl(corporateClient);
  if (!learnMoreUrl) return null;
  return (
    <a href={learnMoreUrl} className='text-brand-500'>
      Learn more about the program
    </a>
  );
};

const NotifyMeModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  return (
    <Modal
      variant='dialog'
      isOpen={isOpen}
      headerDivision='divider'
      onClose={onClose}
      title='Notify me'
      primaryCTA={{
        text: 'Close',
        callback: onClose,
      }}
      ignoreExtraPadding
    >
      <p className='text-center'>
        Thanks! We'll let you know when a new cohort opens so you can contribute again.
      </p>
    </Modal>
  );
};

const getHeaderSubtext = (cohortsClosed: boolean, userCohortCompleted: boolean) => {
  if (!userCohortCompleted) {
    return cohortsClosed ?
        "There are no open cohorts right now, but we'd love to have you join the next one."
      : "Ready to make an impact? A new cohort is open and we're looking for people like you.";
  } else {
    return cohortsClosed ?
        'Thank you for the time and effort you contributed. There are no open cohorts right now.'
      : 'Thank you for the time and effort you contributed. Ready to make an impact again? A new cohort is open.';
  }
};

const getLearnMoreUrl = (corporateClient?: string | null) => {
  if (!corporateClient) return undefined;
  switch (corporateClient) {
    case 'SAP':
      return 'https://movingworlds.org/partner/sap/acceleration-collective-employees';
    case 'F5':
      return 'https://movingworlds.org/partner/F5/volunteer-sprint-employees-wave7';
    case 'Ernst & Young':
      return 'https://movingworlds.org/partner/ey/ey-ScalingAIforGood';
    default:
      return undefined;
  }
};
